import React, { useEffect, useState } from 'react';
import { FormGroup, Row, Col, Spinner } from 'reactstrap';
import { Input as CustomInput } from '@/components/Input';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { errorRenderer } from '@/utils/utility';
import { Button, buttonVariants } from '@/components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { isSignupEmailDomainBlocked } from '@/utils/authSignupEmail';
import { Checkbox } from '@/components/Checkbox';
import styles from './Signup.module.scss';
import { Link } from 'react-router-dom';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { authActionTypes } from '@store/Auth/Auth.actionTypes';

function SignUpForm({ defaultEmail = '', defaultFullName = '' }) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: defaultFullName,
    email: defaultEmail,
    password: '',
    confirm_password: '',
    policy: false,
  });
  const [error, setError] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    policy: '',
  });
  /**
   * Form schemas for validation
   */

  const FormSchema = Yup.object().shape({
    full_name: Yup.string()
      .required('Full Name is required')
      .min(3, 'Name must contain 3 letters'),
    email: Yup.string()
      .email('Email must be a valid email')
      .required('Email address is required')
      .test(
        'blocked-domain',
        'Sign up with this email domain is not allowed.',
        (value) => !isSignupEmailDomainBlocked(value)
      ),
    password: Yup.string()
      .required('Password is required')
      .matches(
        /^(?=.*[!@#$&*])(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z]).{6,}$/,
        'At least 6 Characters, At least one uppercase, one number and one special case character'
      ),
    confirm_password: Yup.string()
      .required('Please confirm your password')
      .oneOf([Yup.ref('password'), null], 'Passwords must match.'),
    policy: Yup.bool().oneOf([true], 'Please accept the terms and conditions.'),
  });

  /**
   * Declarations
   */
  const {
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(FormSchema),
    defaultValues: {
      full_name: defaultFullName,
      email: defaultEmail,
    },
  });
  const dispatch = useDispatch();
  const history = useHistory();
  const signUpAuth = useSelector((state) => ({ ...state.auth.signUp }));

  /**
   * handleSignUpSubmit - handles user sign up process
   * @param {*} data - SignUp Form Data
   */
  useEffect(() => {
    let error = null;
    if (formData.password) {
      const password = formData.password;
      if (
        !/^(?=.*[!@#$&*])(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z]).{6,}$/.test(password)
      ) {
        error =
          'Password must contain at least 6 characters, one uppercase, one lowercase, one number and one special character';
      }
    }
    setError({
      ...error,
      password: error,
    });
  }, [formData.password]);
  useEffect(() => {
    let error = null;
    if (formData.email) {
      const email = formData.email;
      if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
        error = 'Email must be a valid email';
      }
    }
    setError({
      ...error,
      email: error,
    });
  }, [formData.email]);
  useEffect(() => {
    if (formData.policy) {
      setError({
        ...error,
        policy: '',
      });
    }
  }, [formData.policy]);

  const validateForm = () => {
    const errors = {};
    if (!formData.full_name) {
      errors.full_name = 'Full Name is required';
    }
    if (!formData.email) {
      errors.email = 'Email address is required';
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    }
    if (!formData.confirm_password) {
      errors.confirm_password = 'Please confirm your password';
    } else if (formData.password !== formData.confirm_password) {
      errors.confirm_password = 'Passwords must match';
    }
    if (!formData.policy) {
      errors.policy = 'Please accept the terms and conditions.';
    }
    setError(errors);
    return errors;
  };
  const handleSignUpSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }
    setIsLoading(true);
    dispatch({
      type: authActionTypes.SIGN_UP,
      payload: {
        email: formData.email,
        password: formData.password,
        fullName: formData.full_name,
      },
    });
    setIsLoading(false);
  };
  useEffect(() => {
    if (signUpAuth.error) {
      reset();
      history.push('/auth/login');
    }
  }, [signUpAuth]);

  return (
    <form role="form" className={styles.signupForm}>
      <CustomInput
        id="full_name"
        className={styles.input}
        placeholder="Full Name"
        type="text"
        name="full_name"
        value={formData.full_name}
        errorMessage={error.full_name}
        onChange={(e) =>
          setFormData({ ...formData, full_name: e.target.value })
        }
      />
      <CustomInput
        id="email"
        className={styles.input}
        placeholder="Email"
        type="email"
        autoComplete="new-email"
        name="email"
        value={formData.email}
        errorMessage={error.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
      />
      <CustomInput
        id="password"
        className={styles.input}
        placeholder="Password"
        type="password"
        autoComplete="new-password"
        name="password"
        value={formData.password}
        errorMessage={error.password}
        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
      />
      <CustomInput
        id="confirm_password"
        className={styles.input}
        placeholder="Confirm your password"
        type="password"
        autoComplete="confirm-password"
        name="confirm_password"
        value={formData.confirm_password}
        errorMessage={error.confirm_password}
        onChange={(e) =>
          setFormData({ ...formData, confirm_password: e.target.value })
        }
      />
      <Row className="my-2">
        <Col xs="12">
          <Checkbox
            className={styles.signupCheckbox}
            text={
              <span style={{ fontSize: '14px' }}>
                I agree with the{' '}
                <a
                  href="https://prodaff.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
              </span>
            }
            id="policy"
            checked={formData.policy}
            onClick={() => {
              setFormData({ ...formData, policy: !formData.policy });
            }}
          />
          {error.policy && (
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Regular}
              color="error-500"
            >
              {error.policy}
            </Text>
          )}
        </Col>
      </Row>

      <div className="text-center w-100">
        <Button
          variant={buttonVariants.primary_light}
          className={styles.signupFormButton}
          text="Create account"
          disabled={signUpAuth.pending}
          loading={signUpAuth.pending}
          onClick={handleSignUpSubmit}
        />
        {/* <Button
          disabled={signUpAuth.pending}
          className="mt-4 w-50"
          color="primary"
          type="submit"
          onClick={handleSignUpSubmit}
        >
          {signUpAuth.pending ? (
            <Spinner style={{ height: '1rem', width: '1rem' }} />
          ) : (
            'Create account'
          )}
        </Button> */}
      </div>
      <div className={styles.signupFooter}>
        <div className="text-center mt-2">
          <Text
            variant={textVariantsEnum.Text_15}
            fontWeight={fontWeightEnum.Semibold}
          >
            Already have an account? <Link to="/auth/login">Login Here</Link>
          </Text>
        </div>
      </div>
    </form>
  );
}

SignUpForm.propTypes = {
  defaultEmail: PropTypes.string,
  defaultFullName: PropTypes.string,
};

export default SignUpForm;
